/**
 * C.3c — Drag-and-drop folder import via webkitGetAsEntry.
 *
 * Works in Chrome, Edge, Firefox, Safari. Read-only (no writes back to
 * the dropped folder). Reads each file's bytes on traversal so the
 * resulting ImportSource is fully in-memory for the rest of the wizard.
 */

import { isTextExt } from './walk';
import type { ImportFile, ImportSource } from './types';

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
}
interface FileSystemFileEntryLike extends FileSystemEntryLike {
  file: (cb: (file: File) => void, err?: (e: unknown) => void) => void;
}
interface FileSystemDirectoryEntryLike extends FileSystemEntryLike {
  createReader: () => FileSystemDirectoryReaderLike;
}
interface FileSystemDirectoryReaderLike {
  readEntries: (
    cb: (entries: FileSystemEntryLike[]) => void,
    err?: (e: unknown) => void,
  ) => void;
}

function readDirEntries(
  reader: FileSystemDirectoryReaderLike,
): Promise<FileSystemEntryLike[]> {
  return new Promise((resolve, reject) => {
    const out: FileSystemEntryLike[] = [];
    const step = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(out);
        } else {
          out.push(...batch);
          step();
        }
      }, reject);
    };
    step();
  });
}

function readFile(entry: FileSystemFileEntryLike): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

async function* walkEntry(
  entry: FileSystemEntryLike,
  prefix: string,
): AsyncIterableIterator<ImportFile> {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntryLike;
    const file = await readFile(fileEntry);
    const path = prefix ? `${prefix}/${entry.name}` : entry.name;
    const dot = entry.name.lastIndexOf('.');
    const ext = dot > 0 ? entry.name.slice(dot + 1).toLowerCase() : '';
    const isText = isTextExt(ext);
    const out: ImportFile = {
      path,
      ext,
      sizeBytes: file.size,
      lastModifiedMs: file.lastModified,
      mimeType: file.type || undefined,
    };
    if (isText) out.text = await file.text();
    else out.bytes = new Uint8Array(await file.arrayBuffer());
    yield out;
  } else if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntryLike;
    const reader = dirEntry.createReader();
    const entries = await readDirEntries(reader);
    const nextPrefix = prefix ? `${prefix}/${entry.name}` : entry.name;
    for (const child of entries) {
      yield* walkEntry(child, nextPrefix);
    }
  }
}

/**
 * Build an ImportSource from a DragEvent (a folder dropped onto the
 * window). If the drop contains multiple top-level items, they all
 * become siblings under the vault root.
 */
export function buildDragDropSource(event: DragEvent): ImportSource | null {
  const items = event.dataTransfer?.items;
  if (!items || items.length === 0) return null;

  // Collect entries that actually came from a drop — `getAsEntry` is
  // synchronous in browsers that support it.
  const roots: FileSystemEntryLike[] = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i]!;
    const getEntry =
      (it as unknown as { webkitGetAsEntry?: () => FileSystemEntryLike | null })
        .webkitGetAsEntry;
    if (typeof getEntry === 'function') {
      const entry = getEntry.call(it);
      if (entry) roots.push(entry);
    }
  }
  if (roots.length === 0) return null;

  // If the only root is a single directory, label after that directory.
  // Otherwise the label is generic — `roots.length` files.
  const label =
    roots.length === 1 && roots[0]!.isDirectory
      ? `Folder: ${roots[0]!.name}`
      : `${roots.length} dropped items`;

  async function* iter(): AsyncIterableIterator<ImportFile> {
    for (const root of roots) {
      // Strip the dropped folder's own name from `prefix` so the dropped
      // folder behaves like the vault root, matching the SharePoint path.
      if (root.isDirectory) {
        const dirEntry = root as FileSystemDirectoryEntryLike;
        const reader = dirEntry.createReader();
        const children = await readDirEntries(reader);
        for (const child of children) {
          yield* walkEntry(child, '');
        }
      } else {
        yield* walkEntry(root, '');
      }
    }
  }

  return { kind: 'dragdrop', label, files: iter };
}
